import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import QRCode from "react-qr-code";
import { CheckCircle2, Copy, Share2, ArrowLeft, CalendarDays, MapPin, Camera, Link2, FileDown, CalendarPlus, ShieldCheck, HandCoins, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PublicLayout } from "../components/PublicLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { api, formatDateTime } from "../lib/api";
import { withLang } from "../lib/i18n";
import { useLang } from "../context/LanguageContext";

export default function OrderConfirmationPage() {
  const { orderId } = useParams();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/orders/${orderId}`)
      .then((res) => setData(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [orderId]);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => toast.success(t("Kode kopiert")));
  };

  const shareLink = () => {
    navigator.clipboard.writeText(withLang(window.location.href)).then(() => toast.success(t("Lenke kopiert – del den med følget ditt")));
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [gdprStatus, setGdprStatus] = useState(null);
  const [gdprSubmitting, setGdprSubmitting] = useState(false);

  const requestGdprDelete = async () => {
    setGdprSubmitting(true);
    try {
      const res = await api.post(`/orders/${orderId}/gdpr-delete`);
      setGdprStatus(res.data.status);
      toast.success(res.data.message);
    } catch {
      toast.error(t("Kunne ikke registrere sletteforespørselen – prøv igjen"));
    } finally {
      setGdprSubmitting(false);
    }
  };

  const downloadRefundReceipt = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/orders/${orderId}/refund-receipt.pdf`, { responseType: "blob", params: { lang } });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "refusjonskvittering.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("Refusjonskvitteringen er lastet ned som PDF"));
    } catch {
      toast.error(t("Kunne ikke laste ned PDF – prøv igjen"));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/orders/${orderId}/pdf`, { responseType: "blob", params: { lang } });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = lang === "nn" ? "billettar.pdf" : "billetter.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("Billettene er lastet ned som PDF"));
    } catch {
      toast.error(t("Kunne ikke laste ned PDF – prøv igjen"));
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-10 space-y-4">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-80 rounded-2xl" />
        </div>
      </PublicLayout>
    );
  }

  if (notFound || !data) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="order-not-found">
          <h1 className="text-2xl font-semibold mb-2">{t("Fant ikke bestillingen")}</h1>
          <p className="text-muted-foreground mb-6">{t("Sjekk at lenken er riktig.")}</p>
          <Button asChild>
            <Link to="/"><ArrowLeft className="w-4 h-4 mr-1.5" />{t("Til forsiden")}</Link>
          </Button>
        </div>
      </PublicLayout>
    );
  }

  const { order, tickets, event } = data;
  const emailEnabled = !!data.email_enabled;
  const checkedInCount = tickets.filter((t) => t.checked_in_at).length;
  const eventUpcoming = event?.date ? new Date(event.date) > new Date() : false;
  const effectiveGdprStatus = gdprStatus || (order.gdpr_deleted_at ? "deleted" : order.gdpr_delete_requested ? "scheduled" : null);

  if (order.status === "refunded") {
    // Refundert av arrangøren (eller i Vipps-portalen): billettane er ugyldige – vis tydeleg status i staden for QR-kodar
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="order-refunded-view">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <div className="text-center mb-8">
              <div className="w-14 h-14 rounded-full bg-secondary text-foreground flex items-center justify-center mx-auto mb-4">
                <Undo2 className="w-7 h-7" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="order-refunded-title">
                {t("Bestillingen er refundert")}
              </h1>
              <p className="text-muted-foreground mt-2" data-testid="order-refunded-summary">
                {t("{amount} kr er refundert til deg via Vipps", { amount: order.refund_amount ?? order.total_amount })}
                {order.refunded_at ? ` · ${formatDateTime(order.refunded_at)}` : ""}
              </p>
            </div>
            <div className="rounded-2xl border-2 border-border bg-secondary/40 p-5 mb-6 flex items-start gap-3" data-testid="order-refunded-banner">
              <ShieldCheck className="w-6 h-6 shrink-0 mt-0.5" />
              <div className="text-sm space-y-1">
                <p className="font-heading font-semibold">{t("Billettene i denne bestillingen er ikke lenger gyldige")}</p>
                <p>{t("Arrangøren har refundert hele bestillingen. Beløpet går tilbake til kontoen eller kortet du betalte med – det tar vanligvis noen virkedager før det er synlig. Har du spørsmål, ta kontakt med arrangøren.")}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 mb-6" data-testid="order-refunded-actions">
              <Button variant="secondary" onClick={downloadRefundReceipt} disabled={downloadingPdf} data-testid="order-refund-receipt-button">
                <FileDown className="w-4 h-4 mr-1.5" />
                {t("Last ned refusjonskvittering (PDF)")}
              </Button>
              {order.refund_psp_reference && (
                <span className="text-xs text-muted-foreground" data-testid="order-refund-psp">
                  {t("Vipps-referanse")}: <span className="font-mono">{order.refund_psp_reference}</span>
                </span>
              )}
            </div>
            {event && (
              <Card className="mb-6 border-border">
                <CardContent className="p-5">
                  <h2 className="font-heading font-semibold text-lg">{event.title}</h2>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{formatDateTime(event.date)}</span>
                    <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{[event.venue, event.city].filter(Boolean).join(", ")}</span>
                  </div>
                  <Button asChild variant="secondary" size="sm" className="mt-4" data-testid="order-refunded-event-link">
                    <Link to={withLang(`/arrangement/${event.id}`)}>{t("Til arrangementet")}</Link>
                  </Button>
                </CardContent>
              </Card>
            )}
            <div className="text-center">
              <Button asChild variant="ghost" data-testid="order-refunded-home-button">
                <Link to="/"><ArrowLeft className="w-4 h-4 mr-1.5" />{t("Til forsiden")}</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </PublicLayout>
    );
  }

  // Legg til i kalender: Google-lenke eller nedlastbar .ics (Apple/Outlook)
  const toUtcBasic = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");

  const addToGoogleCalendar = () => {
    const start = new Date(event.date);
    const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: event.title,
      dates: `${toUtcBasic(start)}/${toUtcBasic(end)}`,
      details: t("Billettene dine: {url}", { url: window.location.href }),
      location: [event.venue, event.address, event.city].filter(Boolean).join(", "),
    });
    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, "_blank", "noopener");
  };

  const downloadIcs = async () => {
    try {
      const res = await api.get(`/events/${event.id}/kalender.ics`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "arrangement.ics";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("Kalenderfil lastet ned – åpne den for å legge arrangementet i kalenderen"));
    } catch {
      toast.error(t("Kunne ikke lage kalenderfilen – prøv igjen"));
    }
  };

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="order-confirmation-title">
              {t("Billettene dine er klare!")}
            </h1>
            <p className="text-muted-foreground mt-2">
              {order.quantity} {order.quantity === 1 ? t("billett") : t("billetter")} {t("til")} {order.name}
            </p>
          </div>

          {(order.payment_method === "door" || order.status === "pay_at_door") && (
            <div className="rounded-2xl border-2 border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/15 p-5 mb-6 flex items-start gap-3" data-testid="order-pay-at-door-banner">
              <HandCoins className="w-6 h-6 shrink-0 mt-0.5" />
              <div>
                <p className="font-heading font-semibold" data-testid="order-pay-at-door-title">
                  {t("Ikke betalt – betal {n} kr ved inngangen", { n: order.total_amount })}
                </p>
                <p className="text-sm mt-1">
                  {t("Du valgte å betale ved inngangen. Billettene er reservert og gyldige, men betales til arrangøren når du kommer (kontant eller Vipps på stedet). Ha QR-koden klar.")}
                </p>
              </div>
            </div>
          )}

          {event && (
            <Card className="mb-6 border-border">
              <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h2 className="font-heading font-semibold text-lg">{event.title}</h2>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{formatDateTime(event.date)}</span>
                    <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{[event.venue, event.city].filter(Boolean).join(", ")}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="secondary" size="sm" data-testid="ticket-add-calendar-button">
                        <CalendarPlus className="w-4 h-4 mr-1.5" />
                        {t("Legg til i kalender")}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={addToGoogleCalendar} data-testid="calendar-google-item">
                        Google Kalender
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={downloadIcs} data-testid="calendar-ics-item">
                        Apple / Outlook (.ics)
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="secondary" size="sm" onClick={downloadPdf} disabled={downloadingPdf} data-testid="ticket-download-pdf-button">
                    <FileDown className="w-4 h-4 mr-1.5" />
                    {downloadingPdf ? t("Laster ned...") : t("Last ned PDF")}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={shareLink} data-testid="ticket-share-link-button">
                    <Share2 className="w-4 h-4 mr-1.5" />
                    {t("Del lenke")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="rounded-2xl border-2 border-[hsl(var(--ring))] bg-accent text-accent-foreground p-5 mb-6" data-testid="order-link-card">
            <div className="flex items-center gap-2 mb-2">
              <Link2 className="w-4 h-4 shrink-0" />
              <p className="font-medium text-sm">{t("Din billettlenke – ta vare på denne!")}</p>
            </div>
            <p className="text-xs mb-3 opacity-80" data-testid="order-link-email-note">
              {emailEnabled
                ? t("Vi har også sendt lenken til {email}. Lagre den for å åpne billettene igjen senere.", { email: order.email })
                : t("Billettene sendes ikke på e-post. Lagre denne lenken for å åpne billettene igjen senere.")}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate rounded-lg bg-card border border-border px-3 py-2 text-xs font-mono" data-testid="order-link-url">
                {window.location.href}
              </code>
              <Button size="sm" onClick={shareLink} data-testid="order-link-copy-button">
                <Copy className="w-4 h-4 mr-1.5" />
                {t("Kopier")}
              </Button>
            </div>
          </div>

          <div className="rounded-xl bg-accent text-accent-foreground p-4 mb-6 flex items-start gap-3 text-sm">
            <Camera className="w-4 h-4 mt-0.5 shrink-0" />
            <p>{t("Ta gjerne skjermbilde av billettene før du drar, i tilfelle du mister dekning. Vis QR-koden ved inngangen. Det er fri plassering – ingen setenummer.")}</p>
          </div>

          <div className="space-y-6" data-testid="ticket-list">
            {tickets.map((ticket, idx) => (
              <motion.div
                key={ticket.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.05 }}
              >
                <Card className="border-border overflow-hidden">
                  <div className="bg-ink text-ink-foreground px-5 py-3 flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {t("Billett {i} av {n}", { i: idx + 1, n: tickets.length })}
                      {ticket.ticket_type && <span className="ml-2 opacity-80">· {ticket.ticket_type}</span>}
                    </span>
                    <span className="flex items-center gap-1.5 flex-wrap justify-end">
                      {ticket.payment === "door" && (
                        <Badge className="bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] hover:bg-[hsl(var(--warning))]" data-testid={`ticket-unpaid-badge-${idx}`}>
                          <HandCoins className="w-3 h-3 mr-1" />
                          {ticket.unit_price > 0 ? t("Ikke betalt – {n} kr", { n: ticket.unit_price }) : t("Ikke betalt")}
                        </Badge>
                      )}
                      {ticket.checked_in_at ? (
                        <Badge className="bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] hover:bg-[hsl(var(--warning))]">{t("Sjekket inn")}</Badge>
                      ) : (
                        <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]">{t("Gyldig")}</Badge>
                      )}
                    </span>
                  </div>
                  <CardContent className="p-6 sm:p-8 flex flex-col items-center gap-5">
                    <div className="bg-white p-4 rounded-xl border border-border" data-testid="ticket-qr">
                      <QRCode value={ticket.code} size={240} />
                    </div>
                    <div className="text-center space-y-2">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("Billettkode")}</p>
                      <div className="flex items-center gap-2 justify-center">
                        <span className="font-mono font-semibold text-xl tracking-wider" data-testid="ticket-code">{ticket.code}</span>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyCode(ticket.code)} data-testid="ticket-copy-code-button">
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {ticket.holder_name}
                        {ticket.ticket_type && <span> · {ticket.ticket_type}</span>}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {checkedInCount > 0 && (
            <p className="text-center text-sm text-muted-foreground mt-6">
              {t("{n} av {total} billetter er allerede sjekket inn.", { n: checkedInCount, total: tickets.length })}
            </p>
          )}

          <div className="mt-10 rounded-xl border border-border bg-secondary/40 p-4" data-testid="gdpr-section">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
              <div className="flex-1 space-y-2">
                <p className="text-sm font-medium">{t("Personvern")}</p>
                {effectiveGdprStatus === "deleted" ? (
                  <p className="text-sm text-muted-foreground" data-testid="gdpr-status-deleted">
                    {t("Personopplysningene dine (navn, e-post og telefon) er slettet fra denne bestillingen.")}
                  </p>
                ) : effectiveGdprStatus === "scheduled" ? (
                  <p className="text-sm text-muted-foreground" data-testid="gdpr-status-scheduled">
                    {t("Sletteforespørselen er registrert. Opplysningene dine slettes automatisk rett etter arrangementet – billettene er fortsatt gyldige.")}
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {t("Du kan når som helst be om at navn, e-post og telefonnummer knyttet til denne bestillingen slettes.")}{" "}
                      <Link to="/personvern" className="underline underline-offset-4" data-testid="gdpr-privacy-link">{t("Les mer i personvernerklæringen")}</Link>.
                    </p>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" disabled={gdprSubmitting} data-testid="gdpr-delete-button">
                          {t("Be om sletting av mine data (GDPR)")}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="gdpr-confirm-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("Slette personopplysningene dine?")}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {eventUpcoming
                              ? t("Arrangementet er ikke gjennomført ennå. Opplysningene dine (navn, e-post og telefon) slettes derfor automatisk rett etter arrangementet. Billettene og QR-kodene dine forblir gyldige, men du vil ikke lenger motta påminnelser på e-post.")
                              : t("Navn, e-post og telefonnummer knyttet til denne bestillingen slettes umiddelbart og kan ikke gjenopprettes.")}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="gdpr-cancel-button">{t("Avbryt")}</AlertDialogCancel>
                          <AlertDialogAction onClick={requestGdprDelete} data-testid="gdpr-confirm-button">
                            {t("Ja, slett mine data")}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="text-center mt-10">
            <Button asChild variant="secondary">
              <Link to="/"><ArrowLeft className="w-4 h-4 mr-1.5" />{t("Se flere arrangementer")}</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </PublicLayout>
  );
}
